import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db, functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';

// Register new user
export const registerUser = async (email, password, userData) => {
  try {
    // Normalize email (lowercase, trim) for consistent storage
    const normalizedEmail = email.toLowerCase().trim();

    // Create auth account
    const userCredential = await createUserWithEmailAndPassword(auth, normalizedEmail, password);
    const user = userCredential.user;

    // Normalize username (lowercase, trim)
    const normalizedUsername = userData.username ? userData.username.toLowerCase().trim() : '';

    // Update profile with display name
    if (normalizedUsername) {
      await updateProfile(user, {
        displayName: normalizedUsername
      });
    }

    // Create user document in Firestore
    await setDoc(doc(db, 'users', user.uid), {
      uid: user.uid,
      email: normalizedEmail,
      username: normalizedUsername,
      emailVerified: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });

    // Send custom verification email via Cloud Function
    try {
      const sendVerificationEmail = httpsCallable(functions, 'sendVerificationEmail');
      await sendVerificationEmail();
    } catch (emailError) {
      console.error('Error sending verification email:', emailError);
      // Don't fail registration if email fails to send
    }

    return { success: true, user };
  } catch (error) {
    console.error('Registration error:', error);
    return {
      success: false,
      error: error.message || 'Registration failed'
    };
  }
};

// Login user
export const loginUser = async (email, password) => {
  try {
    // Normalize email for consistent login
    const normalizedEmail = email.toLowerCase().trim();
    const userCredential = await signInWithEmailAndPassword(auth, normalizedEmail, password);
    return { success: true, user: userCredential.user };
  } catch (error) {
    console.error('Login error:', error);
    let errorMessage = 'Login failed';
    
    if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
      errorMessage = 'Invalid email or password';
    } else if (error.code === 'auth/too-many-requests') {
      errorMessage = 'Too many failed attempts. Please try again later.';
    }
    
    return { success: false, error: errorMessage };
  }
};

// Logout user
export const logoutUser = async () => {
  try {
    await signOut(auth);
    return { success: true };
  } catch (error) {
    console.error('Logout error:', error);
    return { success: false, error: error.message };
  }
};

// Get current user data from Firestore
export const getCurrentUserData = async (uid) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (userDoc.exists()) {
      return { success: true, data: userDoc.data() };
    }
    return { success: false, error: 'User data not found' };
  } catch (error) {
    console.error('Error fetching user data:', error);
    return { success: false, error: error.message };
  }
};

// Listen to auth state changes
export const onAuthChange = (callback) => {
  return onAuthStateChanged(auth, callback);
};

// Update user profile
export const updateUserProfile = async (uid, updates) => {
  try {
    // Normalize email if present in updates
    const normalizedUpdates = { ...updates };
    if (normalizedUpdates.email) {
      normalizedUpdates.email = normalizedUpdates.email.toLowerCase().trim();
    }

    const userDocRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userDocRef);

    // If user document doesn't exist, create it with default fields
    // This handles cases where registration failed partway through
    if (!userDoc.exists()) {
      console.warn('User document does not exist, creating it now for uid:', uid);

      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('Cannot create user document: no authenticated user');
      }

      // Create complete user document with all required fields
      await setDoc(userDocRef, {
        uid: currentUser.uid,
        email: (currentUser.email || '').toLowerCase(),
        username: currentUser.displayName || '',
        emailVerified: currentUser.emailVerified || false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        ...normalizedUpdates // Include the normalized updates being applied
      });
    } else {
      // Document exists, just update it
      await updateDoc(userDocRef, {
        ...normalizedUpdates,
        updatedAt: new Date().toISOString()
      });
    }

    // Update display name in auth if username changed
    if (updates.username && auth.currentUser) {
      await updateProfile(auth.currentUser, {
        displayName: updates.username
      });
    }

    return { success: true };
  } catch (error) {
    console.error('Error updating profile:', error);
    return { success: false, error: error.message };
  }
};

// Change user password
export const changePassword = async (currentPassword, newPassword) => {
  try {
    const user = auth.currentUser;

    if (!user || !user.email) {
      return { success: false, error: 'No user logged in' };
    }

    // Re-authenticate user before changing password
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);

    // Update password
    await updatePassword(user, newPassword);

    return { success: true };
  } catch (error) {
    console.error('Error changing password:', error);
    let errorMessage = 'Failed to change password';

    if (error.code === 'auth/wrong-password') {
      errorMessage = 'Current password is incorrect';
    } else if (error.code === 'auth/weak-password') {
      errorMessage = 'New password is too weak. Use at least 6 characters.';
    } else if (error.code === 'auth/requires-recent-login') {
      errorMessage = 'Please log out and log back in before changing your password';
    }

    return { success: false, error: errorMessage };
  }
};

// Resend email verification
export const resendVerificationEmail = async () => {
  try {
    const user = auth.currentUser;

    if (!user) {
      return { success: false, error: 'No user logged in' };
    }

    // Check Firestore emailVerified status
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    if (userDoc.exists() && userDoc.data().emailVerified) {
      return { success: false, error: 'Email is already verified' };
    }

    // Call Cloud Function to resend verification email
    const sendVerificationEmail = httpsCallable(functions, 'sendVerificationEmail');
    await sendVerificationEmail();

    return { success: true };
  } catch (error) {
    console.error('Error resending verification email:', error);
    let errorMessage = 'Failed to send verification email';

    if (error.code === 'functions/unauthenticated') {
      errorMessage = 'Please log in to resend verification email';
    } else if (error.message) {
      errorMessage = error.message;
    }

    return { success: false, error: errorMessage };
  }
};

// Verify email with token
export const verifyEmailWithToken = async (token) => {
  try {
    const user = auth.currentUser;

    if (!user) {
      return { success: false, error: 'No user logged in' };
    }

    // Call Cloud Function to verify token
    const verifyEmailToken = httpsCallable(functions, 'verifyEmailToken');
    const result = await verifyEmailToken({ token });

    return { success: true, message: result.data.message };
  } catch (error) {
    console.error('Error verifying email token:', error);
    let errorMessage = 'Failed to verify email';

    if (error.code === 'functions/invalid-argument') {
      errorMessage = 'Invalid verification link';
    } else if (error.code === 'functions/deadline-exceeded') {
      errorMessage = 'Verification link has expired. Please request a new one.';
    } else if (error.message) {
      errorMessage = error.message;
    }

    return { success: false, error: errorMessage };
  }
};

// Send password reset email
export const sendPasswordResetEmail = async (email) => {
  try {
    // Call Cloud Function to send password reset email
    const sendResetEmail = httpsCallable(functions, 'sendPasswordResetEmail');
    const result = await sendResetEmail({ email });

    return { success: true, message: result.data.message };
  } catch (error) {
    console.error('Error sending password reset email:', error);
    let errorMessage = 'Failed to send password reset email';

    if (error.message) {
      errorMessage = error.message;
    }

    return { success: false, error: errorMessage };
  }
};

// Reset password with token
export const resetPasswordWithToken = async (token, newPassword) => {
  try {
    // Call Cloud Function to reset password
    const resetPassword = httpsCallable(functions, 'resetPassword');
    const result = await resetPassword({ token, newPassword });

    return { success: true, message: result.data.message };
  } catch (error) {
    console.error('Error resetting password:', error);
    let errorMessage = 'Failed to reset password';

    if (error.code === 'functions/invalid-argument') {
      errorMessage = error.message || 'Invalid reset link';
    } else if (error.code === 'functions/deadline-exceeded') {
      errorMessage = 'Reset link has expired. Please request a new one.';
    } else if (error.message) {
      errorMessage = error.message;
    }

    return { success: false, error: errorMessage };
  }
};

// Check if username is available
export const checkUsernameAvailability = async (username) => {
  try {
    // Call Cloud Function to check username availability
    const checkUsername = httpsCallable(functions, 'checkUsernameAvailability');
    const result = await checkUsername({ username });

    return {
      success: true,
      available: result.data.available,
      message: result.data.message
    };
  } catch (error) {
    console.error('Error checking username availability:', error);
    let errorMessage = 'Failed to check username availability';

    if (error.code === 'functions/invalid-argument') {
      errorMessage = error.message || 'Invalid username';
    } else if (error.message) {
      errorMessage = error.message;
    }

    return { success: false, error: errorMessage, available: false };
  }
};

// Update user statistics
export const updateUserStatistics = async () => {
  try {
    const user = auth.currentUser;

    if (!user) {
      return { success: false, error: 'No user logged in' };
    }

    // Call Cloud Function to update statistics
    const updateStats = httpsCallable(functions, 'updateUserStatistics');
    const result = await updateStats();

    return {
      success: true,
      statistics: result.data.statistics
    };
  } catch (error) {
    console.error('Error updating user statistics:', error);
    return { success: false, error: error.message || 'Failed to update statistics' };
  }
};
